import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Lock,
  RefreshCw,
  LogOut,
  Mail,
  Building2,
  ShieldCheck,
  Plus,
  Pencil,
  Trash2,
  X,
  MessageSquare,
  Podcast,
  ScrollText,
  Eye,
  Edit3,
  Send,
  Download,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/sections/LanguageToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rf_admin_token";

const I18N = {
  de: {
    title: "Admin",
    logout: "Logout",
    login: {
      hint: "Bearer-Token (aus backend/.env → ADMIN_API_KEY) eingeben.",
      submit: "Anmelden",
      invalid: "Ungültiger Admin-Token.",
      fetchErr: "Fehler beim Laden.",
    },
    tabs: {
      submissions: "Anfragen",
      episodes: "Episoden",
      audit: "Audit-Log",
    },
    common: {
      refresh: "Aktualisieren",
      loading: "Lade…",
      cancel: "Abbrechen",
      save: "Speichern",
      saving: "Speichern…",
      create: "Erstellen",
      edit: "Edit",
      delete: "Löschen",
      add: "Hinzufügen",
    },
    submissions: {
      header: "Kontaktanfragen",
      sub: (n) => `${n} Einträge · neueste zuerst`,
      empty: "Noch keine Anfragen.",
    },
    episodes: {
      header: "Episoden",
      sub: (n) => `${n} Folgen · sortiert nach Veröffentlichung`,
      create: "Neue Folge",
      deleteConfirm: (s) => `Episode "${s}" wirklich löschen?`,
      created: "Episode erstellt.",
      updated: "Episode aktualisiert.",
      deleted: "Episode gelöscht.",
      deleteErr: "Löschen fehlgeschlagen.",
      saveErr: "Speichern fehlgeschlagen.",
      loadErr: "Fehler beim Laden der Episoden.",
    },
    audit: {
      header: "Audit-Log",
      sub: (n) => `${n} Ereignisse · neueste zuerst`,
      empty: "Noch keine Ereignisse.",
      loadErr: "Audit-Log konnte nicht geladen werden.",
      from: "Von",
      to: "Bis",
      apply: "Filtern",
      reset: "Reset",
      exportCsv: "CSV exportieren",
      exportErr: "CSV-Export fehlgeschlagen.",
      exportEmpty: "Keine Einträge für den gewählten Zeitraum.",
    },
    editor: {
      newTitle: "Neue Folge",
      editTitle: (s) => `Bearbeiten · ${s}`,
      slug: "Slug *",
      number: "Nummer *",
      category: "Kategorie *",
      duration: "Dauer *",
      released: "Veröffentlicht (ISO) *",
      cover: "Cover URL *",
      spotify: "Spotify Embed ID / URL",
      apple: "Apple Embed URL",
      epTitle: "Titel *",
      teaser: "Teaser *",
      intro: "Intro (Markdown) *",
      takeaways: "Kernaussagen",
      write: "Schreiben",
      preview: "Vorschau",
      previewEmpty: "Noch nichts zum Vorschauen.",
    },
  },
  en: {
    title: "Admin",
    logout: "Logout",
    login: {
      hint: "Enter the bearer token (from backend/.env → ADMIN_API_KEY).",
      submit: "Sign in",
      invalid: "Invalid admin token.",
      fetchErr: "Failed to load.",
    },
    tabs: {
      submissions: "Submissions",
      episodes: "Episodes",
      audit: "Audit log",
    },
    common: {
      refresh: "Refresh",
      loading: "Loading…",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      create: "Create",
      edit: "Edit",
      delete: "Delete",
      add: "Add",
    },
    submissions: {
      header: "Contact submissions",
      sub: (n) => `${n} entries · newest first`,
      empty: "No submissions yet.",
    },
    episodes: {
      header: "Episodes",
      sub: (n) => `${n} episodes · sorted by release`,
      create: "New episode",
      deleteConfirm: (s) => `Really delete episode "${s}"?`,
      created: "Episode created.",
      updated: "Episode updated.",
      deleted: "Episode deleted.",
      deleteErr: "Delete failed.",
      saveErr: "Save failed.",
      loadErr: "Failed to load episodes.",
    },
    audit: {
      header: "Audit log",
      sub: (n) => `${n} events · newest first`,
      empty: "No events yet.",
      loadErr: "Audit log could not be loaded.",
      from: "From",
      to: "To",
      apply: "Apply",
      reset: "Reset",
      exportCsv: "Export CSV",
      exportErr: "CSV export failed.",
      exportEmpty: "No entries for the selected range.",
    },
    editor: {
      newTitle: "New episode",
      editTitle: (s) => `Edit · ${s}`,
      slug: "Slug *",
      number: "Number *",
      category: "Category *",
      duration: "Duration *",
      released: "Released (ISO) *",
      cover: "Cover URL *",
      spotify: "Spotify Embed ID / URL",
      apple: "Apple Embed URL",
      epTitle: "Title *",
      teaser: "Teaser *",
      intro: "Intro (Markdown) *",
      takeaways: "Key takeaways",
      write: "Write",
      preview: "Preview",
      previewEmpty: "Nothing to preview yet.",
    },
  },
};

function useAdminI18n() {
  const { lang } = useLanguage();
  return useMemo(() => I18N[lang] || I18N.de, [lang]);
}

// ---------- Main page ----------
export default function Admin() {
  const t = useAdminI18n();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [inputToken, setInputToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("submissions");

  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);

  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchSubmissions = async (bearer) => {
    setSubmissionsLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      setSubmissions(res.data || []);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, bearer);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        toast.error(t.login.invalid);
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error(t.login.fetchErr);
      }
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const fetchEpisodes = async () => {
    setEpisodesLoading(true);
    try {
      const res = await axios.get(`${API}/episodes`);
      setEpisodes(res.data || []);
    } catch {
      toast.error(t.episodes.loadErr);
    } finally {
      setEpisodesLoading(false);
    }
  };

  const fetchAudit = async (bearer, params = {}) => {
    setAuditLoading(true);
    try {
      const search = new URLSearchParams();
      if (params.from) search.set("from_ts", params.from);
      if (params.to) search.set("to_ts", params.to);
      if (params.action) search.set("action", params.action);
      search.set("limit", String(params.limit ?? 500));
      const res = await axios.get(`${API}/audit?${search.toString()}`, {
        headers: { Authorization: `Bearer ${bearer || token}` },
      });
      setAudit(res.data || []);
    } catch {
      toast.error(t.audit.loadErr);
    } finally {
      setAuditLoading(false);
    }
  };

  const downloadAuditCsv = async (params = {}) => {
    try {
      const search = new URLSearchParams();
      if (params.from) search.set("from_ts", params.from);
      if (params.to) search.set("to_ts", params.to);
      if (params.action) search.set("action", params.action);
      search.set("format", "csv");
      search.set("limit", "10000");
      const res = await axios.get(`${API}/audit?${search.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      if (!res.data || res.data.size === 0) {
        toast.message(t.audit.exportEmpty);
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      const tag = [params.from || "all", params.to || "now"].join("_").replaceAll(":", "");
      a.href = url;
      a.download = `redflag_audit_${tag}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t.audit.exportErr);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSubmissions(token);
      fetchEpisodes();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authed && tab === "audit") fetchAudit();
  }, [authed, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLogin = (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    setToken(inputToken.trim());
    fetchSubmissions(inputToken.trim());
    fetchEpisodes();
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setSubmissions([]);
    setAudit([]);
    setInputToken("");
  };

  const deleteEpisode = async (slug) => {
    if (!window.confirm(t.episodes.deleteConfirm(slug))) return;
    try {
      await axios.delete(`${API}/episodes/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t.episodes.deleted);
      fetchEpisodes();
    } catch {
      toast.error(t.episodes.deleteErr);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-white/10 bg-[#0F0F0F] p-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock size={22} className="text-redflag" />
              <h1 className="font-display uppercase text-2xl">{t.title}</h1>
            </div>
            <LanguageToggle />
          </div>
          <p className="mt-4 text-sm text-white/55">{t.login.hint}</p>
          <input
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="rf_admin_••••••••••••••••"
            data-testid="admin-token-input"
            className="mt-6 w-full bg-[#0A0A0A] border border-white/15 focus:border-redflag px-4 py-3 text-sm outline-none transition-colors text-white"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="btn-primary mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm uppercase tracking-wide font-medium"
          >
            <ShieldCheck size={16} /> {t.login.submit}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
          <h1 className="font-display uppercase text-2xl">
            <span className="text-redflag">!</span> {t.title}
          </h1>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <LogOut size={14} /> {t.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-5 flex gap-2 overflow-x-auto">
          <TabButton
            active={tab === "submissions"}
            onClick={() => setTab("submissions")}
            testid="admin-tab-submissions"
            icon={<MessageSquare size={14} />}
            label={t.tabs.submissions}
            count={submissions.length}
          />
          <TabButton
            active={tab === "episodes"}
            onClick={() => setTab("episodes")}
            testid="admin-tab-episodes"
            icon={<Podcast size={14} />}
            label={t.tabs.episodes}
            count={episodes.length}
          />
          <TabButton
            active={tab === "audit"}
            onClick={() => setTab("audit")}
            testid="admin-tab-audit"
            icon={<ScrollText size={14} />}
            label={t.tabs.audit}
            count={audit.length}
          />
          <TabButton
            active={tab === "newsletter"}
            onClick={() => setTab("newsletter")}
            testid="admin-tab-newsletter"
            icon={<Mail size={14} />}
            label="Newsletter"
            count={0}
          />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-5 py-12">
        {tab === "submissions" && (
          <SubmissionsView
            t={t}
            submissions={submissions}
            loading={submissionsLoading}
            onRefresh={() => fetchSubmissions(token)}
          />
        )}
        {tab === "episodes" && (
          <EpisodesView
            t={t}
            episodes={episodes}
            loading={episodesLoading}
            onRefresh={fetchEpisodes}
            onCreate={() => setEditingEpisode("new")}
            onEdit={(slug) => setEditingEpisode(slug)}
            onDelete={deleteEpisode}
          />
        )}
        {tab === "audit" && (
          <AuditView
            t={t}
            items={audit}
            loading={auditLoading}
            onRefresh={(params) => fetchAudit(token, params)}
            onExportCsv={downloadAuditCsv}
          />
        )}
        {tab === "newsletter" && (
          <NewsletterView token={token} />
        )}
      </main>

      {editingEpisode && (
        <EpisodeEditor
          t={t}
          slug={editingEpisode === "new" ? null : editingEpisode}
          token={token}
          onClose={() => setEditingEpisode(null)}
          onSaved={() => {
            setEditingEpisode(null);
            fetchEpisodes();
          }}
        />
      )}
    </div>
  );
}

// ---------- Tab Button ----------
function TabButton({ active, onClick, label, icon, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-medium border-b-2 transition-colors whitespace-nowrap ${
        active
          ? "text-redflag border-redflag"
          : "text-white/60 border-transparent hover:text-white"
      }`}
    >
      {icon}
      {label}
      <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/10 rounded-sm">
        {count}
      </span>
    </button>
  );
}

// ---------- Submissions View ----------
function SubmissionsView({ t, submissions, loading, onRefresh }) {
  return (
    <>
      <Section
        header={t.submissions.header}
        sub={t.submissions.sub(submissions.length)}
        right={
          <button
            onClick={onRefresh}
            data-testid="admin-refresh-submissions"
            className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
          >
            <RefreshCw size={14} /> {t.common.refresh}
          </button>
        }
      />
      {loading ? (
        <div className="mt-10 text-white/55">{t.common.loading}</div>
      ) : submissions.length === 0 ? (
        <div className="mt-10 text-white/55">{t.submissions.empty}</div>
      ) : (
        <div className="mt-10 border border-white/10 divide-y divide-white/10">
          {submissions.map((s) => (
            <div
              key={s.id}
              data-testid={`admin-submission-${s.id}`}
              className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 hover:bg-[#0F0F0F] transition-colors"
            >
              <div className="md:col-span-3">
                <div className="font-medium text-white">{s.name}</div>
                <a
                  href={`mailto:${s.email}`}
                  className="mt-1 inline-flex items-center gap-2 text-sm text-redflag hover:underline"
                >
                  <Mail size={13} /> {s.email}
                </a>
                {s.company && (
                  <div className="mt-1 inline-flex items-center gap-2 text-xs text-white/55">
                    <Building2 size={12} /> {s.company}
                  </div>
                )}
              </div>
              <div className="md:col-span-7 text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
                {s.message}
              </div>
              <div className="md:col-span-2 font-mono-plex text-[11px] uppercase tracking-widest text-white/40 md:text-right">
                {new Date(s.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------- Episodes View ----------
function EpisodesView({ t, episodes, loading, onRefresh, onCreate, onEdit, onDelete }) {
  return (
    <>
      <Section
        header={t.episodes.header}
        sub={t.episodes.sub(episodes.length)}
        right={
          <div className="flex items-center gap-3">
            <button
              onClick={onRefresh}
              data-testid="admin-refresh-episodes"
              className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <RefreshCw size={14} /> {t.common.refresh}
            </button>
            <button
              onClick={onCreate}
              data-testid="admin-create-episode"
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <Plus size={14} /> {t.episodes.create}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="mt-10 text-white/55">{t.common.loading}</div>
      ) : (
        <div className="mt-10 border border-white/10 divide-y divide-white/10">
          {episodes.map((ep) => (
            <div
              key={ep.slug}
              data-testid={`admin-episode-row-${ep.slug}`}
              className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center hover:bg-[#0F0F0F] transition-colors"
            >
              <img
                src={ep.cover}
                alt={ep.de?.title}
                className="md:col-span-1 h-14 w-14 object-cover"
              />
              <div className="md:col-span-2 font-mono-plex text-[11px] uppercase tracking-widest text-redflag">
                EP {ep.number} · {ep.category}
              </div>
              <div className="md:col-span-5">
                <div className="font-medium text-white">{ep.de?.title}</div>
                <div className="mt-1 text-xs text-white/55">
                  /podcast/{ep.slug}
                </div>
              </div>
              <div className="md:col-span-2 font-mono-plex text-[11px] uppercase tracking-widest text-white/40">
                {ep.released_iso}
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <button
                  onClick={() => onEdit(ep.slug)}
                  data-testid={`admin-episode-edit-${ep.slug}`}
                  className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wide"
                >
                  <Pencil size={12} /> {t.common.edit}
                </button>
                <button
                  onClick={() => onDelete(ep.slug)}
                  data-testid={`admin-episode-delete-${ep.slug}`}
                  className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wide hover:border-redflag hover:text-redflag"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------- Audit View ----------
function AuditView({ t, items, loading, onRefresh, onExportCsv }) {
  const [actionFilter, setActionFilter] = useState("");
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const toIsoStart = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : "");
  const toIsoEnd = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : "");

  const applyDateRange = () => {
    onRefresh({
      from: toIsoStart(fromDate),
      to: toIsoEnd(toDate),
      action: actionFilter || undefined,
    });
  };

  const resetDateRange = () => {
    setFromDate("");
    setToDate("");
    onRefresh({});
  };

  const exportCsv = () => {
    onExportCsv({
      from: toIsoStart(fromDate),
      to: toIsoEnd(toDate),
      action: actionFilter || undefined,
    });
  };

  const actions = useMemo(() => {
    const set = new Set(items.map((i) => i.action).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (actionFilter && it.action !== actionFilter) return false;
      if (!q) return true;
      return (
        (it.action || "").toLowerCase().includes(q) ||
        (it.ip || "").toLowerCase().includes(q) ||
        JSON.stringify(it.meta || {}).toLowerCase().includes(q)
      );
    });
  }, [items, actionFilter, query]);

  return (
    <>
      <Section
        header={t.audit.header}
        sub={`${filtered.length} / ${items.length} · ${t.audit.sub(items.length).split('·').pop().trim()}`}
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              data-testid="admin-audit-export-csv"
              className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <Download size={14} /> {t.audit.exportCsv}
            </button>
            <button
              onClick={() => onRefresh({})}
              data-testid="admin-refresh-audit"
              className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <RefreshCw size={14} /> {t.common.refresh}
            </button>
          </div>
        }
      />

      {/* Date range bar */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-12 gap-3 items-end">
        <label className="md:col-span-3 flex flex-col gap-1">
          <span className="font-mono-plex text-[10px] uppercase tracking-widest text-white/45">{t.audit.from}</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="audit-date-from"
            className="bg-[#0F0F0F] border border-white/15 focus:border-redflag px-3 py-2.5 text-sm outline-none text-white"
          />
        </label>
        <label className="md:col-span-3 flex flex-col gap-1">
          <span className="font-mono-plex text-[10px] uppercase tracking-widest text-white/45">{t.audit.to}</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="audit-date-to"
            className="bg-[#0F0F0F] border border-white/15 focus:border-redflag px-3 py-2.5 text-sm outline-none text-white"
          />
        </label>
        <button
          onClick={applyDateRange}
          data-testid="audit-apply-range"
          className="md:col-span-2 bg-redflag hover:bg-redflag-deep transition px-4 py-2.5 text-xs uppercase tracking-wider text-white font-semibold"
        >
          {t.audit.apply}
        </button>
        {(fromDate || toDate) && (
          <button
            onClick={resetDateRange}
            data-testid="audit-reset-range"
            className="md:col-span-2 btn-ghost px-3 py-2.5 text-xs uppercase tracking-wide"
          >
            {t.audit.reset}
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search action, IP or meta…"
          data-testid="audit-search"
          className="md:col-span-8 bg-[#0F0F0F] border border-white/15 focus:border-redflag px-4 py-2.5 text-sm outline-none text-white placeholder:text-white/30"
        />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          data-testid="audit-action-filter"
          className="md:col-span-3 bg-[#0F0F0F] border border-white/15 focus:border-redflag px-3 py-2.5 text-sm outline-none text-white"
        >
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        {(actionFilter || query) && (
          <button
            onClick={() => { setActionFilter(""); setQuery(""); }}
            data-testid="audit-clear-filters"
            className="md:col-span-1 btn-ghost px-3 py-2.5 text-xs uppercase tracking-wide"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-10 text-white/55">{t.common.loading}</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 text-white/55">{t.audit.empty}</div>
      ) : (
        <div className="mt-8 border border-white/10 divide-y divide-white/10">
          {filtered.map((entry, i) => (
            <div
              key={entry.id || i}
              data-testid={`admin-audit-row-${i}`}
              className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center text-sm hover:bg-[#0F0F0F]"
            >
              <div className="md:col-span-4 font-mono-plex text-xs text-redflag uppercase tracking-widest">
                {entry.action}
              </div>
              <div className="md:col-span-3 font-mono-plex text-xs text-white/60">
                {entry.ip}
              </div>
              <div className="md:col-span-3 text-xs text-white/55 truncate">
                {entry.meta && Object.keys(entry.meta).length
                  ? JSON.stringify(entry.meta)
                  : "—"}
              </div>
              <div className="md:col-span-2 font-mono-plex text-[11px] uppercase tracking-widest text-white/40 md:text-right">
                {entry.ts ? new Date(entry.ts).toLocaleString() : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Section({ header, sub, right }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <h2 className="overline">{header}</h2>
        <p className="mt-2 font-mono-plex text-sm text-white/55">{sub}</p>
      </div>
      {right}
    </div>
  );
}

// ---------- Episode Editor Modal ----------
const EMPTY_EPISODE = {
  slug: "",
  number: "",
  category: "",
  duration: "",
  released_iso: new Date().toISOString().slice(0, 10),
  cover: "",
  spotify_embed_id: "",
  apple_embed_id: "",
  de: { title: "", teaser: "", intro: "", takeaways: [""] },
  en: { title: "", teaser: "", intro: "", takeaways: [""] },
};

function EpisodeEditor({ t, slug, token, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_EPISODE);
  const [original, setOriginal] = useState(EMPTY_EPISODE);  // snapshot for diff
  const [loading, setLoading] = useState(!!slug);
  const [saving, setSaving] = useState(false);
  const isNew = !slug;

  useEffect(() => {
    if (!slug) {
      setForm(EMPTY_EPISODE);
      setOriginal(EMPTY_EPISODE);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/episodes/${slug}`);
        const data = {
          ...EMPTY_EPISODE,
          ...res.data,
          de: { ...EMPTY_EPISODE.de, ...(res.data.de || {}) },
          en: { ...EMPTY_EPISODE.en, ...(res.data.en || {}) },
        };
        setForm(data);
        setOriginal(JSON.parse(JSON.stringify(data)));
      } catch {
        toast.error(t.login.fetchErr);
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, onClose, t]);

  const set = (path, value) => {
    setForm((s) => {
      const next = { ...s };
      const parts = path.split(".");
      let target = next;
      for (let i = 0; i < parts.length - 1; i++) {
        target[parts[i]] = { ...target[parts[i]] };
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateTakeaway = (l, idx, value) =>
    setForm((s) => {
      const items = [...(s[l]?.takeaways || [])];
      items[idx] = value;
      return { ...s, [l]: { ...s[l], takeaways: items } };
    });
  const addTakeaway = (l) =>
    setForm((s) => ({
      ...s,
      [l]: { ...s[l], takeaways: [...(s[l]?.takeaways || []), ""] },
    }));
  const removeTakeaway = (l, idx) =>
    setForm((s) => {
      const items = (s[l]?.takeaways || []).filter((_, i) => i !== idx);
      return { ...s, [l]: { ...s[l], takeaways: items.length ? items : [""] } };
    });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const cleanLocale = (loc) => ({
      ...loc,
      takeaways: (loc?.takeaways || []).filter((tk) => (tk || "").trim()),
    });
    const fullPayload = {
      ...form,
      de: cleanLocale(form.de),
      en: cleanLocale(form.en),
    };
    try {
      if (isNew) {
        await axios.post(`${API}/episodes`, fullPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(t.episodes.created);
      } else {
        // PATCH-aware: send only fields whose value actually changed
        const cleanOriginal = {
          ...original,
          de: cleanLocale(original.de),
          en: cleanLocale(original.en),
        };
        const patch = {};
        Object.keys(fullPayload).forEach((k) => {
          if (JSON.stringify(fullPayload[k]) !== JSON.stringify(cleanOriginal[k])) {
            patch[k] = fullPayload[k];
          }
        });
        if (Object.keys(patch).length === 0) {
          toast(t.common.save + " · 0 changes");
          setSaving(false);
          return;
        }
        await axios.patch(`${API}/episodes/${slug}`, patch, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(t.episodes.updated);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.episodes.saveErr);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-episode-editor"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
    >
      <div className="w-full max-w-3xl bg-[#0F0F0F] border border-white/10">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0F0F0F] z-10">
          <div className="font-display uppercase text-lg">
            {isNew ? t.editor.newTitle : t.editor.editTitle(slug)}
          </div>
          <button
            onClick={onClose}
            data-testid="editor-close"
            className="text-white/55 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-white/55">{t.common.loading}</div>
        ) : (
          <form onSubmit={onSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t.editor.slug}>
                <input
                  required
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="ep-04-thema"
                  data-testid="editor-slug"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.number}>
                <input
                  required
                  value={form.number}
                  onChange={(e) => set("number", e.target.value)}
                  placeholder="04"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.category}>
                <input
                  required
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="Psychologie"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.duration}>
                <input
                  required
                  value={form.duration}
                  onChange={(e) => set("duration", e.target.value)}
                  placeholder="42 min"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.released}>
                <input
                  required
                  value={form.released_iso}
                  onChange={(e) => set("released_iso", e.target.value)}
                  placeholder="2025-12-15"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.cover}>
                <input
                  required
                  value={form.cover}
                  onChange={(e) => set("cover", e.target.value)}
                  placeholder="https://…/cover.jpg"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.spotify}>
                <input
                  value={form.spotify_embed_id}
                  onChange={(e) => set("spotify_embed_id", e.target.value)}
                  placeholder="3T7B9rW… or https://open.spotify.com/episode/…"
                  className="inp"
                />
              </Field>
              <Field label={t.editor.apple}>
                <input
                  value={form.apple_embed_id}
                  onChange={(e) => set("apple_embed_id", e.target.value)}
                  placeholder="https://embed.podcasts.apple.com/…"
                  className="inp"
                />
              </Field>
            </div>

            {["de", "en"].map((l) => (
              <div key={l} className="border border-white/10 p-5 bg-[#0A0A0A]">
                <div className="font-mono-plex text-[10px] uppercase tracking-widest text-redflag">
                  {l.toUpperCase()}
                </div>
                <div className="mt-4 space-y-4">
                  <Field label={t.editor.epTitle}>
                    <input
                      required
                      value={form[l].title}
                      onChange={(e) => set(`${l}.title`, e.target.value)}
                      className="inp"
                    />
                  </Field>
                  <Field label={t.editor.teaser}>
                    <input
                      required
                      value={form[l].teaser}
                      onChange={(e) => set(`${l}.teaser`, e.target.value)}
                      className="inp"
                    />
                  </Field>
                  <Field label={t.editor.intro}>
                    <MarkdownField
                      t={t}
                      value={form[l].intro}
                      onChange={(v) => set(`${l}.intro`, v)}
                    />
                  </Field>
                  <Field label={t.editor.takeaways}>
                    <div className="space-y-2">
                      {form[l].takeaways.map((tk, i) => (
                        <div key={i} className="flex gap-2">
                          <input
                            value={tk}
                            onChange={(e) =>
                              updateTakeaway(l, i, e.target.value)
                            }
                            placeholder={`Takeaway ${i + 1}`}
                            className="inp flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => removeTakeaway(l, i)}
                            className="btn-ghost px-2"
                            aria-label="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addTakeaway(l)}
                        className="btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-xs uppercase tracking-wide"
                      >
                        <Plus size={12} /> {t.common.add}
                      </button>
                    </div>
                  </Field>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost px-5 py-2.5 text-xs uppercase tracking-wide"
              >
                {t.common.cancel}
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="editor-save"
                className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wide font-medium disabled:opacity-60"
              >
                {saving ? t.common.saving : isNew ? t.common.create : t.common.save}
              </button>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .inp {
          width: 100%;
          background: #0F0F0F;
          border: 1px solid rgba(255,255,255,0.12);
          color: white;
          padding: 0.55rem 0.8rem;
          font-size: 0.875rem;
          outline: none;
        }
        .inp:focus { border-color: #e0121a; }
      `}</style>
    </div>
  );
}

function MarkdownField({ t, value, onChange }) {
  const [mode, setMode] = useState("write");
  return (
    <div className="border border-white/12 bg-[#0F0F0F]">
      <div className="flex border-b border-white/10">
        <button
          type="button"
          onClick={() => setMode("write")}
          data-testid="md-write"
          className={`px-3 py-2 text-[11px] font-mono-plex uppercase tracking-widest inline-flex items-center gap-1.5 ${
            mode === "write"
              ? "text-redflag border-b-2 border-redflag"
              : "text-white/55 hover:text-white"
          }`}
        >
          <Edit3 size={11} /> {t.editor.write}
        </button>
        <button
          type="button"
          onClick={() => setMode("preview")}
          data-testid="md-preview"
          className={`px-3 py-2 text-[11px] font-mono-plex uppercase tracking-widest inline-flex items-center gap-1.5 ${
            mode === "preview"
              ? "text-redflag border-b-2 border-redflag"
              : "text-white/55 hover:text-white"
          }`}
        >
          <Eye size={11} /> {t.editor.preview}
        </button>
      </div>
      {mode === "write" ? (
        <textarea
          required
          rows={6}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="**Bold**, *italic*, [link](https://…), > quote, lists, ##  headings…"
          className="w-full bg-transparent text-white text-sm leading-relaxed px-3 py-3 outline-none resize-vertical font-mono-plex"
        />
      ) : (
        <div className="px-4 py-3 prose-rf text-sm text-white/80 min-h-[150px]">
          {value?.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <span className="text-white/40 italic">{t.editor.previewEmpty}</span>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-white/45">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

// ---------- Newsletter View ----------
function NewsletterView({ token }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/newsletter/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch {
      toast.error("Vorschau konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, []); // eslint-disable-line

  const onSend = async () => {
    if (!window.confirm("Newsletter wirklich an alle Subscribers senden?")) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/newsletter/send`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      if (d.status === "mocked") {
        toast.warning(`MOCKED · ${d.recipient_count} Empfänger · ${d.note}`);
      } else {
        toast.success(`Versendet: ${d.sent} · Fehler: ${d.failed} · Total: ${d.total}`);
      }
    } catch {
      toast.error("Senden fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Section
        header="Newsletter · Sonntags-Briefing"
        sub={
          preview
            ? `${preview.recipient_count} Empfänger · Provider: ${preview.provider}`
            : "Lade…"
        }
        right={
          <div className="flex items-center gap-3">
            <button
              onClick={loadPreview}
              data-testid="newsletter-refresh"
              className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <RefreshCw size={14} /> Vorschau aktualisieren
            </button>
            <button
              onClick={onSend}
              disabled={sending}
              data-testid="newsletter-send"
              className="btn-primary inline-flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-wide font-medium disabled:opacity-60"
            >
              <Send size={14} /> {sending ? "Senden…" : "Jetzt senden"}
            </button>
          </div>
        }
      />
      {loading ? (
        <div className="mt-10 text-white/55">Lade…</div>
      ) : preview ? (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 border border-white/10 bg-[#0F0F0F] p-5">
            <Field label="Subject"><div className="text-sm text-white">{preview.subject}</div></Field>
            <div className="h-4" />
            <Field label="From"><div className="text-sm text-white">{preview.from}</div></Field>
            <div className="h-4" />
            <Field label="Empfänger"><div className="text-sm text-white">{preview.recipient_count}</div></Field>
            <div className="h-4" />
            <Field label="Provider">
              <div className={`text-xs ${preview.provider.startsWith("MOCKED") ? "text-amber-400" : "text-white"}`}>
                {preview.provider}
              </div>
            </Field>
            {preview.provider.startsWith("MOCKED") && (
              <p className="mt-5 text-xs text-white/55 leading-relaxed">
                Setze <code className="font-mono-plex">RESEND_API_KEY</code> in <code className="font-mono-plex">backend/.env</code>, um echte Mails zu versenden.
              </p>
            )}
          </div>
          <div className="lg:col-span-8 border border-white/10 bg-white">
            <iframe
              data-testid="newsletter-preview-iframe"
              title="Newsletter preview"
              srcDoc={preview.html}
              className="w-full"
              style={{ height: 480, border: 0 }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
